import type { TaxJurisdictionRow, TaxPaymentSettingsOverview } from './types';
import { SETUP_STATUSES } from './types';

/** One row of the list: a jurisdiction at one work location (Figma shows one row per jurisdiction and work location). */
export interface TaxListRow {
  key: string;
  jurisdictionKey: string;
  jurisdiction: string;
  /** Short note under the jurisdiction name, for example "CA". Empty for Federal. */
  subtitle: string;
  workLocationId: string | null;
  workLocationName: string;
  address: string | null;
  /** The chips of the "Tax Setup Type" column. */
  chips: string[];
  employees: number;
  status: string;
  verifiedOn: string | null;
  source: TaxJurisdictionRow;
}

export function toListRows(overview: TaxPaymentSettingsOverview): TaxListRow[] {
  const rows: TaxListRow[] = [];
  for (const row of overview.rows) {
    const chips = row.taxComponents && row.taxComponents.length > 0 ? row.taxComponents : [row.setupType];
    if (row.relatedLocations.length === 0) {
      rows.push({
        key: row.jurisdictionKey, jurisdictionKey: row.jurisdictionKey, jurisdiction: row.jurisdiction, subtitle: '', workLocationId: null,
        workLocationName: 'Company-wide', address: null, chips, employees: row.employeesAssigned, status: row.setupStatus, verifiedOn: row.verifiedOn ?? null, source: row,
      });
      continue;
    }
    for (const location of row.relatedLocations) {
      rows.push({
        key: `${row.jurisdictionKey}:${location.id}`, jurisdictionKey: row.jurisdictionKey, jurisdiction: row.jurisdiction,
        subtitle: row.jurisdictionKey.length <= 3 ? row.jurisdictionKey : '', workLocationId: location.id,
        workLocationName: location.status === 'Active' ? location.name : `${location.name} (${location.status})`,
        address: location.address ?? null, chips,
        // One location's own count when the server gave it; the jurisdiction total only when it has a single location.
        employees: location.employeesAssigned ?? (row.relatedLocations.length === 1 ? row.employeesAssigned : 0),
        status: row.setupStatus, verifiedOn: row.verifiedOn ?? null, source: row,
      });
    }
  }
  return rows;
}

export type StatusFilter = 'all' | (typeof SETUP_STATUSES)[number];

export function statusCounts(rows: TaxListRow[]): Record<StatusFilter, number> {
  const counts = { all: rows.length } as Record<StatusFilter, number>;
  for (const status of SETUP_STATUSES) counts[status] = rows.filter((r) => r.status === status).length;
  return counts;
}

/** Search matches the jurisdiction, state code, work location, address, status and tax components. */
export function filterRows(rows: TaxListRow[], status: StatusFilter, search: string): TaxListRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (status !== 'all' && r.status !== status) return false;
    if (!needle) return true;
    return [r.jurisdiction, r.subtitle, r.workLocationName, r.address ?? '', r.status, ...r.chips].some((v) => v.toLowerCase().includes(needle));
  });
}

/** Short label of a status for its filter chip. */
export function statusChipLabel(status: StatusFilter): string {
  switch (status) {
    case 'all': return 'All';
    case 'Setup Needed': return 'Setup Needed';
    case 'Setup Needed - No Employees Assigned Yet': return 'No Employees Yet';
    case 'Setup Recorded - Not Verified': return 'Recorded, Not Verified';
    case 'Needs Tax Review': return 'Needs Tax Review';
    case 'Historical Only': return 'Historical Only';
  }
}

/** CSS tone of a status pill. Deliberately never the green "ready" tone: nothing here is verified. */
export function statusTone(status: string): 'needed' | 'quiet' | 'recorded' | 'historical' | 'review' {
  if (status === 'Setup Recorded - Not Verified') return 'recorded';
  if (status === 'Needs Tax Review') return 'review';
  if (status === 'Historical Only') return 'historical';
  if (status.startsWith('Setup Needed - No Employees')) return 'quiet';
  return 'needed';
}

export function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return y && m && d ? new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : iso;
}

export function formatDateTime(isoUtc: string): string {
  return new Date(isoUtc).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}

export function formatRate(percent: number): string {
  return `${Number(percent.toFixed(4))}%`;
}

/** A dollar amount such as "$411.91". */
export function formatMoney(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
